import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { rememberSupportOrigin } from "../../../utils/helpers/supportOrigin";
import Sidebar from "../../navigation/Sidebar/Sidebar";
import LanguageSelector from "../../LanguageSelector/LanguageSelector";
import TeacherListModal from "../../modals/TeacherListModal/TeacherListModal";
import TeacherOverviewModal from "../../modals/TeacherOverviewModal/TeacherOverviewModal";
import useAuth from "../../../utils/hooks/useAuth";
import useModalsStore from "../../../utils/stores/useModalsStore";
import useDashboardView from "../../../utils/hooks/useDashboardView";
import useOrgScope from "../../../utils/hooks/useOrgScope";
import OrgSelect from "../../OrgSelect/OrgSelect";
import { ACCESS_LEVEL } from "../../../utils/hooks/useAccessLevel";

const PAGE_TITLES = [
  { match: (p) => p === "/", key: "pageTitles.dashboard" },
  { match: (p) => p === "/admin", key: "pageTitles.administration" },
  { match: (p) => p.startsWith("/standards") && p !== "/standards", key: "pageTitles.standardDetail" },
  { match: (p) => p.startsWith("/standards"), key: "pageTitles.standards" },
  { match: (p) => p.startsWith("/heatmap"), key: "pageTitles.heatmap" },
  { match: (p) => p.startsWith("/coverage"), key: "pageTitles.coverage" },
  { match: (p) => p.startsWith("/my-observations"), key: "pageTitles.myObservations" },
  { match: (p) => p.startsWith("/observation"), key: "pageTitles.observation" },
  { match: (p) => p.startsWith("/templates"), key: "pageTitles.templates" },
  { match: (p) => p.startsWith("/users"), key: "pageTitles.users" },
  // The draft board is its own destination (a full-shell takeover). Every other
  // timetable surface is a TAB inside one workspace and shares one title: the
  // active tab already says which panel you are on, and a second, differing
  // title in the header bar would only contradict it. Six entries became two.
  { match: (p) => p.startsWith("/timetable/draft"), key: "pageTitles.timetableBoard" },
  { match: (p) => p.startsWith("/timetable"), key: "pageTitles.timetable" },
  { match: (p) => p.startsWith("/my-materials/") && p !== "/my-materials", key: "pageTitles.materialDetail" },
  { match: (p) => p.startsWith("/my-materials"), key: "pageTitles.learningMaterials" },
  { match: (p) => p.startsWith("/materials"), key: "pageTitles.materials" },
  { match: (p) => p.startsWith("/profile"), key: "pageTitles.profile" },
  // First match wins: the two specific support pages before the list.
  { match: (p) => p === "/support/new", key: "pageTitles.supportNew" },
  { match: (p) => p.startsWith("/support/"), key: "pageTitles.supportTicket" },
  { match: (p) => p.startsWith("/support"), key: "pageTitles.support" },
];

const getPageTitleKey = (pathname) =>
  PAGE_TITLES.find((entry) => entry.match(pathname))?.key || null;

// Routes whose data actually follows the picked school. The selector is shown
// ONLY here — a control that silently changed nothing would be worse than no
// control at all, which is what four page-local copies amounted to for someone
// who then walked into Users or Timetable and found them still pinned to their
// own org. Add a route here in the same change that makes it read its org from
// `useOrgScope()`, never before.
const SCOPE_AWARE_ROUTES = [
  (p) => p === "/",
  (p) => p.startsWith("/standards"),
  (p) => p.startsWith("/heatmap"),
  (p) => p.startsWith("/coverage"),
  (p) => p.startsWith("/users"),
  // Not while conducting a booked visit: its teacher is locked, so the picker
  // would change nothing there. Read the VALUE, not `has()` — Observation.js
  // takes `get("visit") || ""`, so `?visit=` with nothing after it is an
  // ordinary form there, and `has()` would hide the control on a page whose
  // roster still obeys the pick.
  (p, search) =>
    p === "/observation" && !new URLSearchParams(search).get("visit"),
];

const isScopeAware = ({ pathname, search }) =>
  SCOPE_AWARE_ROUTES.some((match) => match(pathname, search));

export default function BaseLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const pathTitleKey = getPageTitleKey(location.pathname);
  const { logoutUser } = useAuth();
  const teacherList = useModalsStore((s) => s.teacherList);
  const teacherOverview = useModalsStore((s) => s.teacherOverview);
  const { view, canSwitch, setView } = useDashboardView();
  // "/" is two different pages behind one path — a teacher's own record and a
  // school's overview — so one title could only ever be wrong for somebody.
  // A user with neither dashboard permission lands on the launchpad and keeps
  // the neutral name.
  const dashboardTitleKey = {
    [ACCESS_LEVEL.TEACHER]: "pageTitles.dashboardTeacher",
    [ACCESS_LEVEL.SCHOOL]: "pageTitles.dashboardSchool",
  }[view];
  const pageTitleKey =
    pathTitleKey === "pageTitles.dashboard"
      ? dashboardTitleKey || pathTitleKey
      : pathTitleKey;
  const {
    canCompareSchools,
    organizations,
    effectiveOrgUuid,
    setSelectedOrgUuid,
  } = useOrgScope();
  // Dual-role users get a School/Teacher switch — only meaningful on Home.
  const showViewToggle = canSwitch && location.pathname === "/";
  // One school picker for the whole app rather than one per page: it is the
  // app's scope, not a chart filter, and a head-office user needs the answer to
  // "which school am I looking at" in the same place on every screen.
  // Self-hides for anyone with a single school (OrgSelect returns null).
  const showOrgSelect = canCompareSchools && isScopeAware(location);

  // Where the user was before opening support: a new ticket carries it, so the
  // staff card says which page the problem is on (support/SupportNew.js).
  useEffect(() => {
    rememberSupportOrigin(location.pathname + location.search);
  }, [location.pathname, location.search]);

  const handleLogout = async () => {
    const providerLogoutUrl = await logoutUser();
    if (providerLogoutUrl) {
      // Leaving the SPA on purpose. The provider ends its own session and
      // returns the browser to the address registered with it — without this
      // hop the next person at a shared computer is one click from signing in
      // as whoever just left.
      window.location.assign(providerLogoutUrl);
      return;
    }
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex h-screen text-slate-800 overflow-hidden bg-slate-50">
      <Sidebar onLogout={handleLogout} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-slate-200 px-6 py-4 shrink-0 z-20">
          {/* One row from lg up, the school picker giving way to the page
              title and the toggles; below that the row wraps, and so do the
              controls, rather than running off a phone. */}
          <div className="max-w-7xl mx-auto flex flex-wrap lg:flex-nowrap items-center justify-between gap-x-4 gap-y-2">
            <h1 className="text-lg font-bold tracking-tight text-slate-900 min-w-0 truncate lg:shrink-0">
              {pageTitleKey ? t(pageTitleKey) : "TELOS"}
            </h1>
            <div className="flex flex-wrap lg:flex-nowrap items-center gap-3 min-w-0 max-w-full">
              {showOrgSelect && (
                <OrgSelect
                  organizations={organizations}
                  value={effectiveOrgUuid}
                  onChange={setSelectedOrgUuid}
                />
              )}
              {showViewToggle && (
                <div className="flex shrink-0 items-center gap-1 p-1 bg-slate-100 rounded-lg">
                  {[
                    { v: ACCESS_LEVEL.SCHOOL, label: t("nav.schoolView") },
                    { v: ACCESS_LEVEL.TEACHER, label: t("nav.teacherView") },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setView(opt.v)}
                      className={`px-3 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider transition ${
                        view === opt.v
                          ? "bg-white text-indigo-600 shadow-sm"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              <LanguageSelector />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {teacherList && <TeacherListModal {...teacherList} />}
      {teacherOverview && <TeacherOverviewModal {...teacherOverview} />}
    </div>
  );
}
